import axios from 'axios';
import Vue from 'vue';
import store from '@/store';
import router from '@/router';

const service = axios.create({
    baseURL: "/api/laplace",
    timeout: 30000, // 增加到 30 秒，避免接口响应慢时被误判为超时
    withCredentials: false
});

// 请求拦截器（保持不变）
service.interceptors.request.use(
    config => {
        const token = store.state.token;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // 如果是 FormData，确保不覆盖 Content-Type，让 axios 自动处理 boundary
        if (config.data instanceof FormData) {
            // 删除可能存在的 Content-Type，让浏览器自动设置（包含 boundary）
            if (config.headers['Content-Type']) {
                delete config.headers['Content-Type'];
            }
            // 调试日志
            console.log('上传文件请求:', {
                baseURL: config.baseURL,
                url: config.url,
                fullURL: config.baseURL + config.url,
                method: config.method,
                headers: { ...config.headers },
                dataType: config.data instanceof FormData ? 'FormData' : typeof config.data,
                formDataKeys: config.data instanceof FormData ? Array.from(config.data.keys()) : []
            });
        }
        return config;
    },
    error => {
        console.error('请求出错:', error);
        return Promise.reject(error);
    }
);

// 响应拦截器
service.interceptors.response.use(
    response => {
        const res = response.data;

        if (res.statusCode !== 100200) {
            return handleBusinessError(res);
        }

        if (isAuthResponse(response)) {
            handleAuthToken(response);
        }

        return res;
    },
    error => {
        return handleNetworkError(error);
    }
);

// 辅助方法
function isAuthResponse(response) {
    const authUrls = ['/login', '/auth'];
    return authUrls.some(url => response.config.url.includes(url));
}

function handleAuthToken(response) {
    const token = response.headers['authorization'] ||
        response.headers['Authorization'] ||
        response.data.data?.token;

    if (token) {
        const pureToken = token.replace(/^Bearer\s+/i, '');

        store.commit('setToken', pureToken);
    }
}

function handleBusinessError(responseData) {
    const errorMsg = responseData.message || '请求失败';
    showError(errorMsg);

    if (responseData.statusCode === 100401) {
        console.log("--------------------")

        handleUnauthorized();
    }

    return Promise.reject({
        isBusinessError: true,
        message: errorMsg,
        code: responseData.statusCode,
        data: responseData
    });
}

function handleNetworkError(error) {
    let errorMsg = '网络异常，请稍后重试';

    if (error.response) {
        // 服务器返回了响应，但状态码不是 2xx
        errorMsg = getHttpErrorMessage(error.response);
        if (error.response.status === 401) {
            handleUnauthorized();
        }
    } else if (error.request) {
        // 请求已发出，但没有收到响应
        if (error.code === 'ECONNABORTED' || error.message && error.message.includes('timeout')) {
            errorMsg = '请求超时，请稍后重试';
        } else {
            errorMsg = '网络连接失败，请检查网络';
        }
    } else {
        // 请求配置错误
        errorMsg = error.message || '请求配置错误';
    }

    // showError(errorMsg);
    return Promise.reject({
        isNetworkError: true,
        message: errorMsg,
        original: error
    });
}

// 工具函数
function showError(message) {
    if (Vue.prototype.$message?.error) {
        Vue.prototype.$message.error(message);
    } else {
        console.error(message);
    }
}

function handleUnauthorized() {
    if (typeof Vue.prototype.$logout === 'function') {
        Vue.prototype.$logout();
    }
    store.commit("setToken", '')
    // 安全跳转逻辑
    if (router.currentRoute.path !== '/login') {
        router.replace('/login').catch(() => {
            window.location.href = '/login';
        });
    }
}

function getHttpErrorMessage(response) {
    const status = response.status;
    const serverMsg = response.data?.message;

    switch (status) {
        case 401: return '登录已过期，请重新登录';
        case 403: return '没有操作权限';
        case 404: return serverMsg || '资源不存在';
        case 500: return serverMsg || '服务器内部错误';
        default: return serverMsg || `请求失败: ${status}`;
    }
}

export default service;
